import { Component, OnInit, TemplateRef, ViewChild, computed, inject, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AlertController } from '@ionic/angular';
import { BehaviorSubject, switchMap } from 'rxjs';

import { AppEvent, EventPairing, EventRegistration, EventResultClaim, EventResultOutcome, EventRound } from './models';
import { ClaimSummary, compareResultClaims } from './claim-comparison';
import { surviveRefreshError } from './refresh-stream';
import { EventService } from './event.service';
import { ResultFormOutcome } from './event-result-form.component';
import { ResultFieldValues, seedResultFieldValues, toResultValuesPayload } from './result-fields';
import { AlertService } from '../global/alert/alert';
import { InputAlert } from '../global/input-alert/input-alert';
import { Modal } from '../global/modal';
import { ThemeService } from '../global';
import { ToastService } from '../global/toast/toast';

/** Sentinel radio value for "no opponent" in the participant-B picker below -- a real userId
 *  never collides with it. */
const BYE_VALUE = '__BYE__';

@Component({
    standalone: false,
    selector: 'abs-round-management',
    template: `
        <abs-page-layout [pageTitle]="'GLOBAL.EVENTS.ROUNDS.TITLE' | translate">
            @if (event(); as event) {
                <!-- base.scss's .buttons container, which carries the gutter margin and spacing
                     the rest of the app's button rows use. The previous .controls class was
                     styled nowhere, so this button sat flush against the page edge. -->
                <div class="buttons">
                    <ion-button [disabled]="generating() || !!roundAwaitingResults()" (click)="generateNextRound(event)">
                        {{ 'GLOBAL.EVENTS.ROUNDS.GENERATE_ROUND' | translate: { round: nextRoundNumber() } }}
                    </ion-button>
                    <!-- Beside this page's own main action, matching where the players' page puts
                         it. Results arrive from their phones, so this page goes stale with nothing
                         the organiser did, and a native build has no browser reload. -->
                    <abs-refresh-button [refreshing]="refreshing()" (refresh)="refresh()"></abs-refresh-button>
                </div>
                <!-- Says why the button above is disabled. The server enforces this too; without
                     the explanation an organiser just sees a dead button. -->
                @if (roundAwaitingResults(); as blocking) {
                    <p class="generate-blocked">
                        {{ 'GLOBAL.EVENTS.ROUNDS.AWAITING_RESULTS' | translate: { round: blocking.roundNumber } }}
                    </p>
                }

                @for (round of rounds(); track round._id) {
                    <ion-card>
                        <ion-card-header>
                            <ion-card-title class="round-title">
                                <span>{{ 'GLOBAL.EVENTS.ROUNDS.ROUND' | translate }} {{ round.roundNumber }}</span>
                                <ion-badge>{{ round.status }}</ion-badge>
                            </ion-card-title>
                        </ion-card-header>
                        <ion-card-content>
                            <div class="round-controls">
                                @if (round.status === 'pending') {
                                    <ion-button size="small" (click)="startRound(round)">
                                        {{ 'GLOBAL.EVENTS.ROUNDS.START' | translate }}
                                    </ion-button>
                                }
                                @if (round.status === 'in_progress') {
                                    <ion-button size="small" (click)="completeRound(round)">
                                        {{ 'GLOBAL.EVENTS.ROUNDS.COMPLETE' | translate }}
                                    </ion-button>
                                    <ion-button size="small" fill="outline" (click)="revertRoundStatus(round, 'pending')">
                                        {{ 'GLOBAL.EVENTS.ROUNDS.REVERT_TO_PENDING' | translate }}
                                    </ion-button>
                                }
                                @if (round.status === 'completed') {
                                    <ion-button size="small" fill="outline" (click)="revertRoundStatus(round, 'in_progress')">
                                        {{ 'GLOBAL.EVENTS.ROUNDS.REVERT_TO_IN_PROGRESS' | translate }}
                                    </ion-button>
                                }
                                @if (!hasAnyResult(round)) {
                                    <ion-button size="small" fill="outline" color="danger" (click)="regenerateRound(round)">
                                        {{ 'GLOBAL.EVENTS.ROUNDS.REGENERATE' | translate }}
                                    </ion-button>
                                    <!-- Same guard as regenerate: both throw the round's pairings
                                         away. This one draws no replacement. -->
                                    <ion-button size="small" fill="outline" color="danger" (click)="discardRound(round)">
                                        {{ 'GLOBAL.EVENTS.ROUNDS.DISCARD' | translate }}
                                    </ion-button>
                                }
                            </div>
                            @if (round.status === 'pending' || round.status === 'in_progress') {
                                <p class="ready-count">
                                    {{
                                        'GLOBAL.EVENTS.ROUNDS.READY_COUNT'
                                            | translate: { ready: readyCounts().get(round.roundNumber) ?? 0, total: totalActiveCount() }
                                    }}
                                </p>
                            }
                            <ion-list>
                                <!--
                                    Deliberately not ion-item's own label/slot="end" layout: that
                                    puts the label and both action buttons in one flex row, which
                                    on a narrow screen squeezes the table number and both player
                                    names down to make room for two buttons. Everything below sits
                                    in ion-item's single unslotted content area instead, so this
                                    component's own CSS decides the shape -- info stacked full
                                    width and prominent, actions in their own row underneath, on
                                    mobile; side by side once there's room for both (see styles).
                                -->
                                @for (pairing of round.pairings; track pairing._id) {
                                    <ion-item lines="full">
                                        <div class="pairing-row">
                                            <div class="pairing-info">
                                                @if (pairing.isBye) {
                                                    <div class="pairing-table">{{ 'GLOBAL.EVENTS.ROUNDS.BYE' | translate }}</div>
                                                    <div class="pairing-players">
                                                        {{ pairing.participantA.displayName || pairing.participantA.userId }}
                                                    </div>
                                                } @else {
                                                    <div class="pairing-table">
                                                        {{ 'GLOBAL.EVENTS.ROUNDS.TABLE' | translate }} {{ pairing.tableNumber }}
                                                    </div>
                                                    <div class="pairing-players">
                                                        {{ pairing.participantA.displayName || pairing.participantA.userId }} vs
                                                        {{ pairing.participantB?.displayName || pairing.participantB?.userId }}
                                                    </div>
                                                    @if (pairing.result) {
                                                        <ion-badge [color]="resultColor(pairing.result.status)">{{
                                                            pairing.result.status
                                                        }}</ion-badge>
                                                        <!-- Winner only, on one line. Passing no result fields to the
                                                             pipe yields just that; the per-field scores live behind
                                                             the icon so a long field list cannot push every pairing
                                                             down the page. -->
                                                        <div class="pairing-result">
                                                            <span>{{ pairing.result | resultSummary: pairing : [] }}</span>
                                                            <ion-button
                                                                class="btn-icon btn-sm"
                                                                fill="clear"
                                                                (click)="viewResult(event, round, pairing)"
                                                            >
                                                                <ion-icon name="information-circle-outline" slot="icon-only"></ion-icon>
                                                            </ion-button>
                                                        </div>
                                                    }
                                                }
                                            </div>
                                            <div class="pairing-actions">
                                                <ion-button size="small" fill="clear" (click)="editPairing(event, round, pairing)">
                                                    {{ 'GLOBAL.EVENTS.ROUNDS.EDIT_PAIRING' | translate }}
                                                </ion-button>
                                                @if (!pairing.isBye) {
                                                    <!-- Never 'warning' here. It is a fill colour, so as clear-fill
                                                         text it is unreadable, and filling it gave this one link a
                                                         background none of its neighbours have. Primary instead:
                                                         same treatment as Edit Pairing beside it, so a disputed
                                                         pairing is emphasised without breaking the row. The dispute
                                                         is already flagged twice anyway -- by the solid status badge
                                                         and by this label -- so it needs no third signal. -->
                                                    @let disputed = pairing.result?.status === 'disputed';
                                                    <ion-button
                                                        size="small"
                                                        [color]="disputed ? 'primary' : 'medium'"
                                                        fill="clear"
                                                        (click)="override(event, round, pairing)"
                                                    >
                                                        {{
                                                            (disputed
                                                                ? 'GLOBAL.EVENTS.ROUNDS.RESOLVE_DISPUTE'
                                                                : 'GLOBAL.EVENTS.ROUNDS.OVERRIDE_RESULT'
                                                            ) | translate
                                                        }}
                                                    </ion-button>
                                                    @if (pairing.result) {
                                                        <!-- Wipes the result back to never-reported. Unlike override
                                                             it records no answer -- for resetting a test run, or a
                                                             result too tangled to correct. -->
                                                        <ion-button
                                                            size="small"
                                                            color="danger"
                                                            fill="clear"
                                                            (click)="clearResult(event, round, pairing)"
                                                        >
                                                            {{ 'GLOBAL.EVENTS.ROUNDS.CLEAR_RESULT' | translate }}
                                                        </ion-button>
                                                    }
                                                }
                                            </div>
                                        </div>
                                    </ion-item>
                                }
                            </ion-list>
                        </ion-card-content>
                    </ion-card>
                }
            }
        </abs-page-layout>

        <ng-template #viewResultModal>
            @if (overrideContext; as ctx) {
                <abs-event-result-form
                    [resultFields]="ctx.event.resultFields ?? []"
                    [readOnly]="true"
                    [leftLabel]="ctx.pairing.participantA.displayName || ctx.pairing.participantA.userId || ''"
                    [rightLabel]="ctx.pairing.participantB?.displayName || ctx.pairing.participantB?.userId || ''"
                    [leftWinLabel]="
                        'GLOBAL.EVENTS.ROUNDS.PARTICIPANT_WINS'
                            | translate: { name: ctx.pairing.participantA.displayName || ctx.pairing.participantA.userId }
                    "
                    [drawLabel]="'GLOBAL.EVENTS.ROUNDS.OUTCOME.DRAW' | translate"
                    [rightWinLabel]="
                        'GLOBAL.EVENTS.ROUNDS.PARTICIPANT_WINS'
                            | translate: { name: ctx.pairing.participantB?.displayName || ctx.pairing.participantB?.userId }
                    "
                    [leftValues]="overrideAValues"
                    [rightValues]="overrideBValues"
                    [outcome]="overrideOutcome"
                ></abs-event-result-form>
            }
        </ng-template>

        <ng-template #overrideModal>
            @if (overrideContext; as ctx) {
                <!-- Only present when there are genuinely two differing accounts
                     (compareResultClaims returns nothing otherwise), so a plain override of an
                     undisputed result sees this modal exactly as it was. Two lines rather than
                     two more result forms: the organiser needs to see WHERE the accounts differ
                     and then decide, and a third stacked form would have made the modal
                     unusable on a phone without answering that question any better. -->
                @if (claimComparison.length) {
                    <div class="claim-comparison">
                        <p class="claim-comparison-heading">{{ 'GLOBAL.EVENTS.ROUNDS.CLAIMS_DIFFER' | translate }}</p>
                        @for (line of claimComparison; track line.reporterName) {
                            <div class="claim-line">
                                <div class="claim-line-who">
                                    {{ 'GLOBAL.EVENTS.ROUNDS.PLAYER_REPORTED' | translate: { name: line.reporterName } }}
                                </div>
                                <div class="claim-line-summary">
                                    <span [class.claim-differs]="line.outcomeDiffers">
                                        @if (line.outcome === 'DRAW') {
                                            {{ 'GLOBAL.EVENTS.ROUNDS.OUTCOME.DRAW' | translate }}
                                        } @else {
                                            {{ 'GLOBAL.EVENTS.ROUNDS.PARTICIPANT_WINS' | translate: { name: line.winnerName } }}
                                        }
                                    </span>
                                    @for (field of line.fields; track field.label) {
                                        <span class="claim-sep">·</span>
                                        <span [class.claim-differs]="field.differs">{{ field.label }} {{ field.text }}</span>
                                    }
                                </div>
                                <!-- Most disputes are one player mis-entering one figure, so the
                                     organiser adopts the other's account wholesale. Seeds the form
                                     below rather than submitting, so a mixed decision is still
                                     editable afterwards. -->
                                <ion-button class="btn-xs" fill="clear" (click)="useClaim($event, ctx.event, line.claim)">
                                    {{ 'GLOBAL.EVENTS.ROUNDS.USE_THIS_REPORT' | translate }}
                                </ion-button>
                            </div>
                        }
                    </div>
                    <p class="claim-comparison-heading">{{ 'GLOBAL.EVENTS.ROUNDS.YOUR_DECISION' | translate }}</p>
                }
                <abs-event-result-form
                    [resultFields]="ctx.event.resultFields ?? []"
                    [leftLabel]="ctx.pairing.participantA.displayName || ctx.pairing.participantA.userId || ''"
                    [rightLabel]="ctx.pairing.participantB?.displayName || ctx.pairing.participantB?.userId || ''"
                    [leftWinLabel]="
                        'GLOBAL.EVENTS.ROUNDS.PARTICIPANT_WINS'
                            | translate: { name: ctx.pairing.participantA.displayName || ctx.pairing.participantA.userId }
                    "
                    [drawLabel]="'GLOBAL.EVENTS.ROUNDS.OUTCOME.DRAW' | translate"
                    [rightWinLabel]="
                        'GLOBAL.EVENTS.ROUNDS.PARTICIPANT_WINS'
                            | translate: { name: ctx.pairing.participantB?.displayName || ctx.pairing.participantB?.userId }
                    "
                    [leftValues]="overrideAValues"
                    [rightValues]="overrideBValues"
                    [outcome]="overrideOutcome"
                    (outcomeChange)="overrideOutcome = $event"
                    (validChange)="overrideValid = $event"
                ></abs-event-result-form>
            }
        </ng-template>

        <ng-template #overrideModalButtons>
            <!-- buttonsTemplate replaces the modal's default Close, so it is restated here.
                 No handler: a click in the button row is outside the card, which
                 ModalComponent's host listener already treats as a dismiss. -->
            <ion-button>{{ 'GLOBAL.CLOSE' | translate }}</ion-button>
            <ion-button [disabled]="!overrideValid" (click)="submitOverride()">
                {{ 'GLOBAL.EVENTS.ROUNDS.OVERRIDE_CONFIRM' | translate }}
            </ion-button>
        </ng-template>
    `,
    styles: [
        `
            /* Fills ion-item's own unslotted content area (see the template comment above
               the pairing list) so this decides the shape instead of the default label/end-slot
               row, which is what squeezed the table number and both player names to make room
               for two buttons on a narrow screen. */
            .pairing-row {
                display: flex;
                flex-direction: column;
                width: 100%;
                padding: 10px 0;
                gap: 8px;
            }

            /* The two most prominent facts about a pairing -- the players and the table --
               get the visual weight; everything else (badges, buttons) is secondary. */
            .pairing-table {
                font-weight: 700;
                font-size: 1.05rem;
            }

            .pairing-players {
                font-size: 1rem;
            }

            .pairing-actions {
                display: flex;
                gap: 8px;
            }

            .pairing-actions ion-button {
                flex: 1 1 0;
                margin: 0;
            }

            @media (min-width: 768px) {
                .pairing-row {
                    flex-direction: row;
                    align-items: center;
                    justify-content: space-between;
                    gap: 16px;
                }

                .pairing-actions {
                    flex: 0 0 auto;
                }

                .pairing-actions ion-button {
                    flex: 0 0 auto;
                }
            }
        `
    ]
})
export class RoundManagementPage implements OnInit {
    private eventService = inject(EventService);
    private route = inject(ActivatedRoute);
    private alertService = inject(AlertService);
    private modal = inject(Modal);
    private themeService = inject(ThemeService);

    @ViewChild('viewResultModal') viewResultModal: TemplateRef<any>;
    @ViewChild('overrideModal') overrideModal: TemplateRef<any>;
    @ViewChild('overrideModalButtons') overrideModalButtons: TemplateRef<any>;

    /** Which pairing the open override modal is editing. */
    overrideContext: { event: AppEvent; round: EventRound; pairing: EventPairing } | null = null;

    /** Both players' accounts when they disagree; empty whenever there is nothing to compare.
     *  A plain field, not a computed, because overrideContext is one too -- the whole override
     *  state is seeded imperatively just before the modal opens. */
    claimComparison: ClaimSummary[] = [];
    overrideAValues: ResultFieldValues = {};
    overrideBValues: ResultFieldValues = {};
    overrideOutcome: ResultFormOutcome | null = null;
    overrideValid = false;
    private alertController = inject(AlertController);
    private inputAlert = inject(InputAlert);
    private translateService = inject(TranslateService);
    private toastService = inject(ToastService);

    event = signal<AppEvent | null>(null);
    rounds = signal<EventRound[]>([]);
    registrations = signal<EventRegistration[]>([]);
    generating = signal(false);

    totalActiveCount = computed(() => this.registrations().filter((r) => r.status === 'active').length);
    readyCounts = computed(() => {
        const registrations = this.registrations();
        const counts = new Map<number, number>();
        for (const round of this.rounds()) {
            counts.set(round.roundNumber, registrations.filter((r) => r.readyForRound === round.roundNumber).length);
        }
        return counts;
    });

    private refresh$ = new BehaviorSubject<void>(undefined);

    /** True while a manual refresh is in flight, so the button cannot be queued up twice. */
    readonly refreshing = signal(false);

    ngOnInit() {
        // The event joins the refresh too, not just the rounds: resultFields comes from it,
        // so an organiser who adds a result field in another tab needs it re-read as well.
        this.route.paramMap
            .pipe(
                switchMap((params) =>
                    this.refresh$.pipe(switchMap(() => this.eventService.getEvent(params.get('eventId')!).pipe(surviveRefreshError(() => this.refreshFailed()))))
                )
            )
            .subscribe((event) => this.event.set(event));

        this.route.paramMap
            .pipe(
                switchMap((params) =>
                    this.refresh$.pipe(switchMap(() => this.eventService.getRounds(params.get('eventId')!).pipe(surviveRefreshError(() => this.refreshFailed()))))
                )
            )
            .subscribe((rounds) => {
                this.rounds.set(rounds);
                // Cleared on the rounds fetch specifically: it is the data this page is about,
                // and the one the organiser pressed refresh to see.
                this.refreshing.set(false);
            });

        this.route.paramMap
            .pipe(
                switchMap((params) =>
                    this.refresh$.pipe(
                        switchMap(() => this.eventService.listRegistrations(params.get('eventId')!).pipe(surviveRefreshError(() => this.refreshFailed())))
                    )
                )
            )
            .subscribe((registrations) => this.registrations.set(registrations));
    }

    /** Clears the in-flight flag and says so, without ending the stream. See surviveRefreshError. */
    private refreshFailed() {
        this.refreshing.set(false);
        this.toastService.showToast('', this.translateService.instant('GLOBAL.EVENTS.REFRESH_FAILED'), {
            color: 'danger',
            duration: 4000
        });
    }

    /** Manual reload. A native build has no browser reload, so without this an organiser
     *  watching results come in during a round has no way to pull in new reports. */
    refresh() {
        this.refreshing.set(true);
        this.refresh$.next();
    }

    generateNextRound(event: AppEvent) {
        const nextRoundNumber = this.rounds().length + 1;
        this.generating.set(true);
        this.eventService.startRound(event._id!, nextRoundNumber).subscribe({
            next: () => {
                this.generating.set(false);
                this.refresh$.next();
            },
            error: () => this.generating.set(false)
        });
    }

    startRound(round: EventRound) {
        this.eventService
            .updateRoundStatus(round.eventId, round._id!, { status: 'in_progress', startedAt: new Date().toISOString() })
            .subscribe(() => this.refresh$.next());
    }

    completeRound(round: EventRound) {
        this.eventService
            .updateRoundStatus(round.eventId, round._id!, { status: 'completed', completedAt: new Date().toISOString() })
            .subscribe(() => this.refresh$.next());
    }

    /** No confirmation step -- unlike regenerateRound this never discards data, it only
     *  moves the round's status/timestamps backwards. */
    revertRoundStatus(round: EventRound, status: 'pending' | 'in_progress') {
        this.eventService.updateRoundStatus(round.eventId, round._id!, { status }).subscribe(() => this.refresh$.next());
    }

    hasAnyResult(round: EventRound): boolean {
        return round.pairings.some((p) => !!p.result);
    }

    /**
     * The earliest round still missing a result on a real pairing, if any. The next round
     * cannot be drawn until this is cleared -- byes are skipped, having nobody to report
     * against. Mirrors the server's own check in RoundService.generateRound.
     */
    readonly roundAwaitingResults = computed<EventRound | undefined>(() =>
        [...this.rounds()]
            .sort((a, b) => a.roundNumber - b.roundNumber)
            .find((round) => round.pairings.some((p) => !p.isBye && !p.result))
    );

    /** The number the next generated round will carry, for the button's label. */
    readonly nextRoundNumber = computed(() => this.rounds().reduce((max, r) => Math.max(max, r.roundNumber), 0) + 1);

    async discardRound(round: EventRound) {
        const result = await this.alertService.showConfirm(
            this.translateService.instant('GLOBAL.EVENTS.ROUNDS.DISCARD_TITLE', { round: round.roundNumber }),
            this.translateService.instant('GLOBAL.EVENTS.ROUNDS.DISCARD_PROMPT'),
            {
                ok: this.translateService.instant('GLOBAL.EVENTS.ROUNDS.DISCARD_CONFIRM'),
                cancel: this.translateService.instant('GLOBAL.CANCEL')
            }
        );
        if (result.role !== 'ok') return;

        this.eventService.discardRound(round.eventId, round._id!).subscribe({
            next: () => this.refresh$.next(),
            error: () =>
                this.toastService.showToast('', this.translateService.instant('GLOBAL.EVENTS.ROUNDS.DISCARD_FAILED'), {
                    color: 'danger',
                    duration: 3000
                })
        });
    }

    async regenerateRound(round: EventRound) {
        const result = await this.alertService.showConfirm(
            this.translateService.instant('GLOBAL.EVENTS.ROUNDS.REGENERATE_TITLE', { round: round.roundNumber }),
            this.translateService.instant('GLOBAL.EVENTS.ROUNDS.REGENERATE_PROMPT'),
            {
                ok: this.translateService.instant('GLOBAL.EVENTS.ROUNDS.REGENERATE_CONFIRM'),
                cancel: this.translateService.instant('GLOBAL.CANCEL')
            }
        );
        if (result.role !== 'ok') return;

        this.eventService.regenerateRound(round.eventId, round._id!).subscribe({
            next: () => this.refresh$.next(),
            error: () =>
                this.toastService.showToast('', this.translateService.instant('GLOBAL.EVENTS.ROUNDS.REGENERATE_FAILED'), {
                    color: 'danger',
                    duration: 3000
                })
        });
    }

    /**
     * Three-step alert sequence (this app's AlertController has no multi-input-group support,
     * so participant A, participant B/bye, and the table number are each their own prompt --
     * the same pattern promptForFieldValuesAndOverride below already uses for a per-field loop).
     * Cancelling any step aborts the whole edit; nothing is saved until the last step commits.
     */
    async editPairing(event: AppEvent, round: EventRound, pairing: EventPairing) {
        const activeRegs = this.registrations().filter((r) => r.status === 'active');
        const currentAId = pairing.participantA?.userId;
        const currentBId = pairing.participantB?.userId;

        const aAlert = await this.alertController.create({
            header: this.translateService.instant('GLOBAL.EVENTS.ROUNDS.EDIT_PAIRING_PARTICIPANT_A'),
            inputs: activeRegs.map((r) => ({
                type: 'radio' as const,
                label: r.displayName || r.userId,
                value: r.userId,
                checked: r.userId === currentAId
            })),
            buttons: [
                { text: this.translateService.instant('GLOBAL.CANCEL'), role: 'cancel' },
                { text: this.translateService.instant('GLOBAL.OK'), role: 'ok' }
            ]
        });
        await aAlert.present();
        const aResult = await aAlert.onDidDismiss();
        const newAId: string | undefined = aResult.data?.values;
        if (aResult.role !== 'ok' || !newAId) return;

        const bAlert = await this.alertController.create({
            header: this.translateService.instant('GLOBAL.EVENTS.ROUNDS.EDIT_PAIRING_PARTICIPANT_B'),
            inputs: [
                ...activeRegs
                    .filter((r) => r.userId !== newAId)
                    .map((r) => ({
                        type: 'radio' as const,
                        label: r.displayName || r.userId,
                        value: r.userId,
                        checked: r.userId === currentBId
                    })),
                {
                    type: 'radio' as const,
                    label: this.translateService.instant('GLOBAL.EVENTS.ROUNDS.BYE'),
                    value: BYE_VALUE,
                    checked: !currentBId
                }
            ],
            buttons: [
                { text: this.translateService.instant('GLOBAL.CANCEL'), role: 'cancel' },
                { text: this.translateService.instant('GLOBAL.OK'), role: 'ok' }
            ]
        });
        await bAlert.present();
        const bResult = await bAlert.onDidDismiss();
        const bValue: string | undefined = bResult.data?.values;
        if (bResult.role !== 'ok' || bValue === undefined) return;
        const isBye = bValue === BYE_VALUE;

        let tableNumber: number | null = pairing.tableNumber ?? null;
        if (!isBye) {
            const tableInput = await this.inputAlert.show(
                this.translateService.instant('GLOBAL.EVENTS.ROUNDS.EDIT_PAIRING_TABLE_NUMBER'),
                this.translateService.instant('GLOBAL.EVENTS.ROUNDS.EDIT_PAIRING_TABLE_NUMBER_PROMPT'),
                String(pairing.tableNumber ?? ''),
                'number',
                true
            );
            if (tableInput === undefined || tableInput === null) return;
            tableNumber = tableInput === '' ? null : Number(tableInput);
        } else {
            tableNumber = null;
        }

        this.eventService
            .editPairing(event._id!, round._id!, pairing._id!, {
                participantAUserId: newAId,
                participantBUserId: isBye ? null : bValue,
                isBye,
                tableNumber
            })
            .subscribe({
                next: () => this.refresh$.next(),
                error: (err) => {
                    // Surfaces the server's own reason when it sent one, rather than a fixed
                    // message that would hide genuinely different failures behind identical text.
                    const message = err?.error?.message || this.translateService.instant('GLOBAL.EVENTS.ROUNDS.EDIT_PAIRING_FAILED');
                    this.toastService.showToast('', message, { color: 'danger', duration: 4000 });
                }
            });
    }

    /**
     * Opens the same result form a player reports with, rather than the per-field alert
     * chain this used to run (an outcome alert plus two prompts per result field, in
     * sequence). Pre-filled from the existing result, since an override is nearly always
     * a correction to something already reported.
     */
    override(event: AppEvent, round: EventRound, pairing: EventPairing) {
        this.seedOverrideState(event, round, pairing);

        this.modal.show({
            props: {
                title: this.translateService.instant('GLOBAL.EVENTS.ROUNDS.RESOLVE_DISPUTE'),
                contentTemplate: this.overrideModal,
                buttonsTemplate: this.overrideModalButtons,
                buttonsDirection: 'horizontal',
                cssClass: `${this.themeService.theme()} no-background`
            }
        });
    }

    async clearResult(event: AppEvent, round: EventRound, pairing: EventPairing) {
        const result = await this.alertService.showConfirm(
            this.translateService.instant('GLOBAL.EVENTS.ROUNDS.CLEAR_RESULT_TITLE'),
            this.translateService.instant('GLOBAL.EVENTS.ROUNDS.CLEAR_RESULT_PROMPT'),
            {
                ok: this.translateService.instant('GLOBAL.EVENTS.ROUNDS.CLEAR_RESULT_CONFIRM'),
                cancel: this.translateService.instant('GLOBAL.CANCEL')
            }
        );
        if (result.role !== 'ok') return;

        this.eventService.clearResult(event._id!, round._id!, pairing._id!).subscribe({
            next: () => this.refresh$.next(),
            error: (err) => {
                const message = err?.error?.message || this.translateService.instant('GLOBAL.EVENTS.ROUNDS.CLEAR_RESULT_FAILED');
                this.toastService.showToast('', message, { color: 'danger', duration: 4000 });
            }
        });
    }

    /** Loads a pairing's stored result into the form state both modals below render. */
    private seedOverrideState(event: AppEvent, round: EventRound, pairing: EventPairing) {
        const resultFields = event.resultFields ?? [];
        this.overrideContext = { event, round, pairing };
        this.claimComparison = compareResultClaims(pairing.result, pairing, resultFields);
        this.overrideAValues = seedResultFieldValues(resultFields, pairing.result?.participantAValues);
        this.overrideBValues = seedResultFieldValues(resultFields, pairing.result?.participantBValues);
        this.overrideOutcome = pairing.result
            ? pairing.result.outcome === 'DRAW'
                ? 'DRAW'
                : pairing.result.outcome === 'A_WIN'
                  ? 'LEFT'
                  : 'RIGHT'
            : null;
        this.overrideValid = false;
    }

    /**
     * Adopts one player's account as the starting point for the organiser's decision.
     *
     * Replaces the value objects rather than mutating them, so the form's ngOnChanges fires
     * and it re-emits its own validity -- otherwise overrideValid would stay stale and leave
     * Confirm disabled over a complete set of figures.
     *
     * stopPropagation because ModalComponent dismisses on any click reaching its host, and
     * this click must not close the modal it is seeding.
     */
    useClaim(clickEvent: Event, event: AppEvent, claim: EventResultClaim) {
        clickEvent.stopPropagation();
        const resultFields = event.resultFields ?? [];
        this.overrideAValues = seedResultFieldValues(resultFields, claim.participantAValues);
        this.overrideBValues = seedResultFieldValues(resultFields, claim.participantBValues);
        this.overrideOutcome = claim.outcome === 'DRAW' ? 'DRAW' : claim.outcome === 'A_WIN' ? 'LEFT' : 'RIGHT';
    }

    /** Read-only look at a reported result. Same state and same form as override below,
     *  so the two can never drift into showing different things. */
    viewResult(event: AppEvent, round: EventRound, pairing: EventPairing) {
        this.seedOverrideState(event, round, pairing);
        this.modal.show({
            props: {
                title: this.translateService.instant('GLOBAL.EVENTS.ROUNDS.REPORTED_RESULT'),
                contentTemplate: this.viewResultModal,
                cssClass: `${this.themeService.theme()} no-background`
            }
        });
    }

    submitOverride() {
        const context = this.overrideContext;
        if (!context || !this.overrideValid) return;
        const { event, round, pairing } = context;
        const resultFields = event.resultFields ?? [];

        this.eventService
            .overrideResult(event._id!, round._id!, pairing._id!, {
                // LEFT is participant A's column -- see the form's leftLabel in the template.
                outcome: (this.overrideOutcome === 'DRAW' ? 'DRAW' : this.overrideOutcome === 'LEFT' ? 'A_WIN' : 'B_WIN') as EventResultOutcome,
                participantAValues: toResultValuesPayload(resultFields, this.overrideAValues),
                participantBValues: toResultValuesPayload(resultFields, this.overrideBValues)
            })
            .subscribe({
                next: () => this.refresh$.next(),
                error: (err) => {
                    // Surfaces the server's own reason when it sent one (e.g. an organiser
                    // edited resultFields while this alert sequence was open, so the field set
                    // submitted here no longer matches), rather than a fixed message that would
                    // hide genuinely different failures behind identical text.
                    const message = err?.error?.message || this.translateService.instant('GLOBAL.EVENTS.ROUNDS.OVERRIDE_RESULT_FAILED');
                    this.toastService.showToast('', message, { color: 'danger', duration: 4000 });
                }
            });
    }

    resultColor(status: string): string {
        switch (status) {
            case 'confirmed':
            case 'overridden':
                return 'success';
            case 'disputed':
                return 'danger';
            default:
                return 'medium';
        }
    }
}
